/**
 * User Dashboard Page
 * Overview of user's documents, stats, and quick actions
 */

import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FileText,
  FilePlus,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  ArrowRight,
  Eye,
  Edit,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDocuments } from '@/hooks/useDocuments';
import { ROUTES, getDocumentEditorRoute } from '@/constants/routes';
import { DOCUMENT_STATUS, formatDate, getRelativeTime } from '@/constants/beStudyMockData';
import { DocumentStatusBadge } from '@/components/be-study/common/StatusBadge';
import { SimpleProgressBar } from '@/components/be-study/common/ProgressBar';
import { ConfidenceBadge } from '@/components/be-study/common/ConfidenceIndicator';
import { cn } from '@/lib/utils';

function MetricCard({ title, value, icon: Icon, color = 'blue', subtitle }) {
  const colorMap = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  };

  return (
    <Card data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-3.5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={cn('p-2 rounded-lg', colorMap[color])}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function UserDashboard() {
  const { currentUser } = useAuth();
  const { documents } = useDocuments();
  const navigate = useNavigate();

  const myDocs = useMemo(() => {
    return documents.filter(d => d.createdBy === currentUser?.id);
  }, [documents, currentUser]);

  const stats = useMemo(() => ({
    total: myDocs.length,
    inProgress: myDocs.filter(d => d.status === DOCUMENT_STATUS.DRAFT || d.status === DOCUMENT_STATUS.IN_PROGRESS).length,
    pendingReview: myDocs.filter(d => d.status === DOCUMENT_STATUS.PENDING_REVIEW || d.status === DOCUMENT_STATUS.IN_REVIEW).length,
    approved: myDocs.filter(d => d.status === DOCUMENT_STATUS.APPROVED).length,
    rejected: myDocs.filter(d => d.status === DOCUMENT_STATUS.REJECTED).length,
  }), [myDocs]);

  const recentDocs = useMemo(() => {
    return [...myDocs].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).slice(0, 5);
  }, [myDocs]);

  return (
    <BaseLayout title="My Dashboard">
      <div className="p-4 space-y-4" data-testid="user-dashboard">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="dashboard-welcome">
              Welcome back, {currentUser?.name?.split(' ')[0] || 'User'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Here's an overview of your documents
            </p>
          </div>
          <Button asChild data-testid="create-document-btn">
            <Link to={ROUTES.DOCUMENT_CREATE}>
              <FilePlus className="h-4 w-4 mr-2" />
              Create Document
            </Link>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-grid">
          <MetricCard title="Total Documents" value={stats.total} icon={FileText} color="blue" />
          <MetricCard title="In Progress" value={stats.inProgress} icon={Loader2} color="amber" subtitle="Drafts & active" />
          <MetricCard title="Under Review" value={stats.pendingReview} icon={Send} color="purple" subtitle="Pending & in review" />
          <MetricCard title="Approved" value={stats.approved} icon={CheckCircle2} color="green" />
        </div>

        {/* Recent Documents */}
        <Card data-testid="recent-documents-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-semibold">Recent Documents</CardTitle>
            <Button variant="ghost" size="sm" asChild data-testid="view-all-docs-btn">
              <Link to={ROUTES.DOCUMENTS}>
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentDocs.length === 0 ? (
              <div className="text-center py-10" data-testid="no-documents-message">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No documents yet.</p>
                <Button variant="outline" size="sm" className="mt-3" asChild>
                  <Link to={ROUTES.DOCUMENT_CREATE}>Create your first document</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="recent-documents-list">
                {recentDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(getDocumentEditorRoute(doc.id))}
                    data-testid={`recent-doc-${doc.id}`}
                  >
                    <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {doc.templateName} &middot; Updated {getRelativeTime(doc.updatedAt)}
                      </p>
                    </div>
                    <div className="hidden md:block w-28">
                      <SimpleProgressBar value={doc.progress} size="sm" />
                    </div>
                    <div className="hidden lg:block">
                      {doc.confidenceScore > 0 && <ConfidenceBadge score={doc.confidenceScore} size="sm" />}
                    </div>
                    <DocumentStatusBadge status={doc.status} size="sm" />
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); navigate(getDocumentEditorRoute(doc.id)); }}
                        data-testid={`edit-doc-${doc.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats Summary */}
        {stats.rejected > 0 && (
          <Card className="border-red-200 dark:border-red-900" data-testid="rejected-alert">
            <CardContent className="p-4 flex items-center gap-3">
              <XCircle className="h-5 w-5 text-red-500 shrink-0" />
              <div>
                <p className="text-sm font-medium">
                  {stats.rejected} document{stats.rejected > 1 ? 's' : ''} rejected
                </p>
                <p className="text-xs text-muted-foreground">
                  Review feedback and resubmit for approval.
                </p>
              </div>
              <Button variant="outline" size="sm" className="ml-auto shrink-0" asChild>
                <Link to={ROUTES.DOCUMENTS}>View</Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </BaseLayout>
  );
}

export default UserDashboard;
